import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import BusinessForm from "@/components/BusinessForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminMemberships from "@/pages/admin/AdminMemberships";
import { Spinner } from "@/components/States";
import {
  Plus, Search, Pencil, Trash2, Eye, EyeOff, LogOut, Store, Tag, Star,
  LayoutGrid, Award,
} from "lucide-react";

const TABS = [
  { key: "businesses", label: "Negocios", icon: Store },
  { key: "categories", label: "Categorías", icon: LayoutGrid },
  { key: "memberships", label: "Membresías", icon: Award },
];

function BusinessesSection() {
  const { getCategory } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/businesses", { params: { include_inactive: true } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (b) => {
    const r = await api.patch(`/admin/businesses/${b.id}/toggle`);
    toast.success(r.data.is_active ? "Negocio activado" : "Negocio desactivado");
    load();
  };
  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar "${b.name}"? Esta acción no se puede deshacer.`)) return;
    await api.delete(`/admin/businesses/${b.id}`);
    toast.success("Negocio eliminado");
    load();
  };

  const filtered = items.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));
  const activeCount = items.filter((b) => b.is_active).length;
  const promoCount = items.filter((b) => b.discount?.badge || b.discount?.title).length;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Negocios", value: items.length, icon: Store, color: "#1A1A1A" },
          { label: "Activos", value: activeCount, icon: Eye, color: "#16A34A" },
          { label: "Con promo", value: promoCount, icon: Tag, color: "#E54D2E" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl bg-white p-4">
            <s.icon size={18} style={{ color: s.color }} />
            <p className="mt-2 font-display text-2xl font-black">{s.value}</p>
            <p className="text-xs font-semibold text-[#5C5C5C]">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-black/5 bg-white px-3 py-2.5">
          <Search size={16} className="text-[#5C5C5C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar negocio..." data-testid="admin-search" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-business-button" className="flex items-center gap-1.5 rounded-xl bg-[#E54D2E] px-4 py-2.5 text-sm font-bold text-white">
          <Plus size={18} /> Nuevo
        </button>
      </div>

      {loading ? <Spinner /> : (
        <div className="mt-4 overflow-hidden rounded-2xl bg-white">
          {filtered.map((b) => {
            const cat = getCategory(b.category);
            const CatIcon = cat.icon;
            return (
              <div key={b.id} data-testid={`admin-row-${b.id}`} className="flex items-center gap-3 border-b border-black/5 px-4 py-3 last:border-0">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#F3F0E6]">
                  {b.hero_image_url && <img src={imgUrl(b.hero_image_url)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-bold text-[#1A1A1A]">{b.name}</p>
                    {b.membership && (
                      <span className="rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase text-white" style={{ backgroundColor: b.membership.color }}>
                        {b.membership.badge_label || b.membership.name}
                      </span>
                    )}
                    {b.featured && <Star size={13} className="fill-[#E54D2E] text-[#E54D2E]" />}
                  </div>
                  <p className="flex items-center gap-1 truncate text-xs text-[#5C5C5C]"><CatIcon size={11} style={{ color: cat.color }} /> {cat.label} · {b.city}</p>
                </div>
                {!b.is_active && <span className="rounded-full bg-[#F3F0E6] px-2 py-0.5 text-[10px] font-bold text-[#5C5C5C]">Inactivo</span>}
                <button onClick={() => toggle(b)} data-testid={`toggle-${b.id}`} className="rounded-lg p-2 hover:bg-[#F3F0E6]">
                  {b.is_active ? <Eye size={17} className="text-[#16A34A]" /> : <EyeOff size={17} className="text-[#5C5C5C]" />}
                </button>
                <button onClick={() => { setEditing(b); setFormOpen(true); }} data-testid={`edit-${b.id}`} className="rounded-lg p-2 hover:bg-[#F3F0E6]"><Pencil size={17} className="text-[#1A1A1A]" /></button>
                <button onClick={() => remove(b)} data-testid={`delete-${b.id}`} className="rounded-lg p-2 hover:bg-red-50"><Trash2 size={17} className="text-[#E54D2E]" /></button>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="px-4 py-10 text-center text-sm text-[#5C5C5C]">Sin negocios.</p>}
        </div>
      )}

      {formOpen && <BusinessForm initial={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("businesses");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F3F0E6]">
      <header className="sticky top-0 z-30 border-b border-black/5 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div>
            <p className="font-display text-xl font-black tracking-tight">Panel Saipass</p>
            <p className="text-xs text-[#5C5C5C]">{admin?.email}</p>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-full bg-[#F3F0E6] px-3 py-2 text-sm font-bold">
            <LogOut size={16} /> Salir
          </button>
        </div>
        <div className="mx-auto mt-3 flex max-w-4xl gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                tab === t.key ? "bg-[#1A1A1A] text-white" : "bg-[#F3F0E6] text-[#1A1A1A]"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-6">
        {tab === "businesses" && <BusinessesSection />}
        {tab === "categories" && <AdminCategories />}
        {tab === "memberships" && <AdminMemberships />}
      </main>
    </div>
  );
}
